import { EMPTY_STRING } from '../../../../../common/constants/grimsby';
import { ClassroomStatus } from '../../../../interfaces/activity';
import { getStageName } from '../../../../../common/utils/amplify';

type ClassroomsConfig = {
    prod: string;
    gamma: string;
    beta: string;
    sam: string;
    [key: string]: string;
};

export const classroomStatusAlertMap = {
    [ClassroomStatus.COURSE_ALLOWED_FOR_CLASSROOM_CREATION]: {
        type: 'pending',
        message: 'Eligible',
    },
    [ClassroomStatus.CLASSROOM_READY]: {
        type: 'success',
        message: 'Ready',
    },
    [ClassroomStatus.CANCELLED]: {
        type: 'stopped',
        message: 'Cancelled',
    },
    [ClassroomStatus.READY_FOR_CLASSROOM_CREATION]: {
        type: 'in-progress',
        message: `In progress`,
    },
    [ClassroomStatus.ELIGIBLE_FOR_CLASSROOM_CREATION]: {
        type: null,
        message: EMPTY_STRING,
    },
    [ClassroomStatus.CLASSROOM_UPDATE_FAILED]: {
        type: 'error',
        message: 'Failed',
    },
    [ClassroomStatus.CLASSROOM_PROVISIONING_FAILED]: {
        type: 'error',
        message: 'Failed',
    },
};

// Friendly names to backend enum values (for filtering)
// Fully derived from classroomStatusAlertMap - groups backend keys by their message
export const CLASSROOM_STATUS_FILTER_TO_BACKEND: Record<string, string[]> =
    (() => {
        const mapping: Record<string, string[]> = {};

        for (const [backendKey, config] of Object.entries(
            classroomStatusAlertMap,
        )) {
            const friendlyName = config.message;

            // Skip empty messages
            if (!friendlyName) continue;

            // Group backend keys by friendly name
            // Since a couple backend failures are just displayed as "Failed" in UI
            if (!mapping[friendlyName]) {
                mapping[friendlyName] = [];
            }
            mapping[friendlyName].push(backendKey);
        }

        return mapping;
    })();

// Backend enum values to friendly names (for displaying saved filters)
// Auto-generated from classroomStatusAlertMap to maintain single source of truth
export const CLASSROOM_STATUS_BACKEND_TO_FRIENDLY: Record<string, string> =
    (() => {
        const mapping: Record<string, string> = {};
        for (const [backendKey, config] of Object.entries(
            classroomStatusAlertMap,
        )) {
            if (config.message) {
                mapping[backendKey] = config.message;
            }
        }
        return mapping;
    })();

const DEFAULT_CLASSROOM_URLS: ClassroomsConfig = {
    prod: 'https://instructor.skillbuilder.aws/',
    gamma: 'https://instructor.skillbuilder.training.aws.dev/',
    beta: 'https://us-west-2.beta.instructor.classrooms.aws.training/',
    sam: 'https://us-west-2.beta.instructor.classrooms.aws.training/',
};

const getClassroomUrl = (): string => {
    try {
        // Get configuration and current stage
        const config = window.__CONFIGURATION__ || {};

        if ((config as any).CLASSROOM_URL) {
            return (config as any).CLASSROOM_URL;
        }

        // Fallback logic
        const classrooms = DEFAULT_CLASSROOM_URLS;
        const stageName = getStageName().toLowerCase();

        if (classrooms[stageName]) {
            return classrooms[stageName];
        }

        // Any other value should return prod URL
        return DEFAULT_CLASSROOM_URLS.prod;
    } catch (error) {
        // If anything fails, return the default prod URL
        return DEFAULT_CLASSROOM_URLS.prod;
    }
};

export const CLASSROOM_CONSTANTS = {
    URLS: {
        get CLASSROOMS_PORTAL() {
            return getClassroomUrl();
        },
    },
    MESSAGES: {
        UNSUPPORTED_COURSE:
            'Automatic classroom creation is not supported for this course.',
        CREATE_MANUALLY: 'Create classroom manually.',
    },
    // DEPRECATED. Please add unsupported courses to CLASSROOM_CONFIG in classroomEligibilityRules.ts
    // Courses added into this list will just display a warning to the user.
    UNSUPPORTED_COURSES: [].map((c) => c.toLowerCase()),
} as const;
